<template>
    <Transition name="quickFade">
        <div
            v-show="visible"
            class="fixed inset-0 z-50 flex items-center justify-center overscroll-none bg-background-accent/80 p-4 backdrop-blur-sm"
            @click.self="onBackdrop"
        >
            <section
                class="flex min-h-0 w-full max-w-[1080px] flex-col overflow-hidden rounded border border-primary bg-background shadow-xl"
                :class="tall ? 'h-[90dvh] max-h-[90dvh]' : 'max-h-[70dvh]'"
                role="dialog"
                aria-modal="true"
            >
                <header class="shrink-0 border-b border-primary bg-primary-light p-4 text-2xl font-bold text-primary">
                    <slot name="title">{{ title }}</slot>
                </header>

                <div
                    class="flex min-h-0 grow flex-col bg-background p-6"
                    :class="contentScrollable ? 'overflow-y-auto' : 'overflow-hidden'"
                >
                    <slot name="content"><slot/></slot>
                </div>

                <footer
                    v-if="$slots.footer || !buttonless"
                    class="flex shrink-0 flex-row justify-end gap-4 border-t border-background-accent-dark px-6 py-4"
                >
                    <slot name="footer">
                        <YButton
                            :type="type === 'normal' ? 'primary' : type"
                            :disabled="disabled"
                            :tooltip="{ key: 'Enter' }"
                            @click.prevent="onOk"
                        >
                            {{ okLabel }}
                        </YButton>
                        <YButton
                            v-if="!okOnly"
                            type="basic"
                            text-only
                            :tooltip="{ key: 'Escape' }"
                            @click.prevent="onCancel"
                        >
                            {{ cancelLabel }}
                        </YButton>
                    </slot>
                </footer>
            </section>
        </div>
    </Transition>
</template>

<script setup lang="ts">

const props = withDefaults(defineProps<{
    title?: string
    disabled?: boolean
    tall?: boolean
    buttonless?: boolean
    okOnly?: boolean
    okLabel?: string
    cancelLabel?: string
    type?: 'normal' | 'destructive'
    closeOnBackdrop?: boolean
    closeOnEscape?: boolean
    contentScrollable?: boolean
}>(), {
    title: '',
    disabled: false,
    tall: false,
    buttonless: false,
    okOnly: false,
    okLabel: 'Ok',
    cancelLabel: 'Cancel',
    type: 'normal',
    closeOnBackdrop: false,
    closeOnEscape: true,
    contentScrollable: true,
})

const visible = defineModel<boolean>({ default: false })
const emit = defineEmits<{
    ok: []
    cancel: []
}>()

function toggleVisible() {
    visible.value = !visible.value
}

function onOk() {
    if (props.disabled) return
    visible.value = false
    emit('ok')
}

function onCancel() {
    visible.value = false
    emit('cancel')
}

function onBackdrop() {
    if (props.closeOnBackdrop) onCancel()
}

function onWindowKeydown(event: KeyboardEvent) {
    if (!visible.value) return

    if (event.key === 'Escape' && props.closeOnEscape) {
        event.preventDefault()
        onCancel()
        return
    }

    if (event.key !== 'Enter' || props.disabled || event.isComposing) return

    const target = event.target as HTMLElement | null
    if (target?.closest('button, textarea, [contenteditable="true"]')) return

    event.preventDefault()
    onOk()
}

// Shared across instances so a popup opened on top of another doesn't unlock the page when it closes.
const lockCount = useState('popup-scroll-lock-count', () => 0)
let holdsLock = false

function setScrollLock(lock: boolean) {
    if (import.meta.server || lock === holdsLock) return
    holdsLock = lock
    lockCount.value += lock ? 1 : -1
    document.documentElement.style.overflow = lockCount.value > 0 ? 'hidden' : ''
}

watch(visible, setScrollLock)

onMounted(() => {
    window.addEventListener('keydown', onWindowKeydown)
    setScrollLock(visible.value)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onWindowKeydown)
    setScrollLock(false)
})

defineExpose({ toggleVisible })
</script>

<style scoped lang="scss">
.quickFade-enter-active,
.quickFade-leave-active {
    transition: opacity 0.15s ease;
}

.quickFade-enter-from,
.quickFade-leave-to {
    opacity: 0;
}
</style>
